import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import Form from 'react-bootstrap/Form'
import Button from '@mui/material/Button';
import apiClient from "../apiClient";
import { useSnackbar } from 'notistack';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';

interface MlModalFormProps {
  showCampaignModal: boolean;
  setShowCampaignModal: Function;
  setUpdateCampaignFilter: Function;
};

export default function CampaignModalForm({
  showCampaignModal,
  setShowCampaignModal,
  setUpdateCampaignFilter
}: MlModalFormProps) {

  // notification
  const { enqueueSnackbar } = useSnackbar();

  // loading state
  const [isLoading, setIsLoading] = useState(false);

  // created ID state
  const defaultId = 'Not created yet';
  const [createdId, setCreatedId] = useState<string>(defaultId);

  // copy to clipboard
  const [isCopied, setIsCopied] = useState(false);

  // disable/enable submit button
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

  // form stats
  const [campaignFacilitator, setCampaignFacilitator] = useState<string>('');
  const [campaignName, setCampaignName] = useState<string>('');
  const [campaignDescription, setCampaignDescription] = useState<string>('');
  const [accessLevel, setAccessLevel] = useState<string>('private');

  // handle API call
  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();

    try {
      setIsLoading(true);

      // API call
      const params = {
        campaign_code: createdId,
        campaign_facilitator: campaignFacilitator,
        campaign_name: campaignName,
        campaign_description: campaignDescription,
        access_level: accessLevel
      };
      const { data } = await apiClient.post('/sigcap/api/create-campaign/', params);

      if (data.created) {
        // update campaign filter list
        setUpdateCampaignFilter((prev: boolean) => !prev);
        enqueueSnackbar('Campaign ID created successfully', {
          variant: 'success',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        });
      }
      else {
        enqueueSnackbar('Campaign ID already exists. Please try again.', {
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        });
      }

    } catch (error) {
      enqueueSnackbar('Error while creating Campaign ID', {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center'
        }
      });
    } finally {
      setIsLoading(false);
      setIsSubmitDisabled(false);
      setShowCampaignModal(false);
      setCreatedId(defaultId);
      setCampaignFacilitator('');
      setCampaignName('');
      setCampaignDescription('');
      setAccessLevel('private');
    }
  };

  // copy to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(createdId);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // create campaign ID
  useEffect(() => {
    // Fetch the wordlist on component mount
    fetch('/four_letter_words.txt')
    .then((res) => res.text())
    .then((text) => {
      const lines = text
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => line.length === 4); // ensure 4-letter words

      // Pick one randomly right after loading
      if (lines.length > 0) {
        const randomIndex = Math.floor(Math.random() * lines.length);
        // setRandomWord(lines[randomIndex]);
        setCreatedId(`${lines[randomIndex].toUpperCase()}${Math.floor(Math.random() * 10)}${Math.floor(Math.random() * 10)}`);
      }
    })
    .catch((error) => {
      console.error('Failed to load wordlist:', error);
    });
  }, [showCampaignModal]);

  return (
    <Modal
      show={showCampaignModal}
      // close modal when pressing X button at the top right
      onHide={() => {
        setShowCampaignModal(false);
        setIsSubmitDisabled(false);
        setCreatedId(defaultId);
        setCampaignFacilitator('');
        setCampaignName('');
        setCampaignDescription('');
        setAccessLevel('private');
      }}
      centered={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Create Campaign ID</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          Created ID is:
          <div
            className="alert alert-info"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr repeat(1, auto) 1fr',
              justifyItems: 'center',
              alignItems: 'center'
            }}
          >
            <div style={{
             marginRight: 'auto',
             visibility: 'hidden'
            }}></div>
            <div
              style={{ gridColumnStart: 2 }}
            >
              <strong>{createdId}</strong>
            </div>
            <Button
              variant="contained"
              size="small"
              color="primary"
              startIcon={<ContentCopyRoundedIcon />}
              onClick={copyToClipboard}
              disabled={createdId === defaultId || createdId === 'Creating...'}
              style={{ marginLeft: 'auto' }}
            >
              {isCopied ? "Copied!" : "Copy"}
            </Button>
          </div>
          <Form.Group >
              <Form.Label
                style={{ marginTop: '1em' }}
              >
                Campaign Facilitator
              </Form.Label>
              <Form.Control
                type="text"
                onChange={(e) => setCampaignFacilitator(e.target.value)}
                value={campaignFacilitator}
              />
              <Form.Label
                style={{ marginTop: '1em' }}
              >
                Campaign Name <span style={{ color: 'red', fontStyle: 'italic', fontSize: '0.85em' }}>required*</span>
              </Form.Label>
              <Form.Control
                type="text"
                onChange={(e) => setCampaignName(e.target.value)}
                value={campaignName}
              />
              <Form.Label
                style={{ marginTop: '1em' }}
              >
                Data Visibility
              </Form.Label>
              <Form.Select
                value={accessLevel}
                onChange={(e) => setAccessLevel(e.target.value)}
              >
                <option value="private">Private - Only owner can access</option>
                <option value="authenticated">Authenticated - Any logged in user can access</option>
                <option value="public">Public - Anyone can access</option>
              </Form.Select>
              <Form.Label
                style={{ marginTop: '1em' }}
              >
                Campaign Description
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                onChange={(e) => setCampaignDescription(e.target.value)}
                value={campaignDescription}
              />
          </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outlined"
          color="primary"
          style={{ marginRight: '1em' }}
          onClick={() => {
            setShowCampaignModal(false);
            setIsSubmitDisabled(false);
            setCreatedId(defaultId);
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          // disable if campaign name is empty
          disabled={isSubmitDisabled || !campaignName.trim()}
          onClick={handleSubmit}
        >
          {isLoading ? 'Creating...' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
